// "What day is it" for the trip, always in the trip's timezone (HANDOVER §7): the pack's, or the
// stage under way's own (B13).
import type { Pack, Stage } from '../../shared/pack/schema'
import { tripNow } from '../../shared/tripZone'
import { daysBetween } from './time'

export type TripClock =
  | {
      phase: 'pre'
      today: string
      time: string
      daysToGo: number
      firstStage: Stage
    }
  | {
      phase: 'during'
      today: string
      time: string
      dayNumber: number // 1-based
      totalDays: number
      stage: Stage
      stageIndex: number
      dayOfStage: number // 1-based
      stageDays: number
      nextStage: Stage | undefined
    }
  | {
      phase: 'after'
      today: string
      time: string
      daysSince: number
      totalDays: number
    }

export function tripClock(pack: Pick<Pack, 'meta' | 'itinerary'>, nowMs: number): TripClock {
  const { meta, itinerary } = pack
  // In the zone of the stage under way (B13: a stage may have its own).
  const { date: today, time } = tripNow(pack, nowMs)
  const totalDays = daysBetween(meta.startDate, meta.endDate) + 1
  const stages = itinerary.stages

  if (today < meta.startDate) {
    const firstStage = stages[0]
    if (!firstStage) throw new Error('pack has no stages')
    return { phase: 'pre', today, time, daysToGo: daysBetween(today, meta.startDate), firstStage }
  }
  if (today > meta.endDate) {
    return { phase: 'after', today, time, daysSince: daysBetween(meta.endDate, today), totalDays }
  }
  const stageIndex = stages.findIndex((s) => s.startDate <= today && today <= s.endDate)
  const stage = stages[stageIndex]
  if (!stage) throw new Error(`no stage covers ${today}`)
  return {
    phase: 'during',
    today,
    time,
    dayNumber: daysBetween(meta.startDate, today) + 1,
    totalDays,
    stage,
    stageIndex,
    dayOfStage: daysBetween(stage.startDate, today) + 1,
    stageDays: daysBetween(stage.startDate, stage.endDate) + 1,
    nextStage: stages[stageIndex + 1],
  }
}
